import { CANVAS_SIZES, CUSTOM_SIZE_ID } from '../../components/canvas/CanvasSizes';
import { parseCanvasStructure, type CanvasStructure } from '../../components/canvas/CanvasDocument';
import type { CanvasImage } from '../../services/canvasService';

// What the editor needs from a saved canvas. Travels as navigation state from the list (which already
// fetched it) so the editor opens without a second request; on a refresh the editor fetches it itself.
export interface CanvasEditorState {
  id: string;
  name: string;
  width: number;
  height: number;
  sizeId: string;
  backgroundColor: string;
  structure: unknown;
  modifiedAt: string | null;
}

export interface CanvasEditorConfig extends Omit<CanvasEditorState, 'structure'> {
  structure: CanvasStructure | null;
}

const sizeIdFor = (width: number, height: number) =>
  CANVAS_SIZES.find((size) => size.width === width && size.height === height)?.id ?? CUSTOM_SIZE_ID;

export const toEditorState = ({ id, data }: CanvasImage): CanvasEditorState => ({
  id,
  name: data.title,
  width: data.width,
  height: data.height,
  sizeId: sizeIdFor(data.width, data.height),
  backgroundColor: data.bg_color,
  structure: data.structure,
  modifiedAt: data.modified_at ?? null,
});

const isValidSide = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const isHexColor = (value: unknown): value is string => typeof value === 'string' && /^#[\da-f]{6}$/i.test(value);

// Validates the navigation state for the canvas `id`; null means it has to be fetched.
export const parseEditorState = (state: unknown, id: string): CanvasEditorConfig | null => {
  const config = state as Partial<CanvasEditorState> | null;
  if (!config || config.id !== id || !isValidSide(config.width) || !isValidSide(config.height)) return null;

  const structure = parseCanvasStructure(config.structure);
  return {
    id,
    name: typeof config.name === 'string' ? config.name : '',
    width: config.width,
    height: config.height,
    // The size preset saved by the editor wins over the one guessed from the dimensions.
    sizeId: structure?.settings.sizeId || (typeof config.sizeId === 'string' ? config.sizeId : sizeIdFor(config.width, config.height)),
    backgroundColor: isHexColor(config.backgroundColor) ? config.backgroundColor : '#FFFFFF',
    structure,
    modifiedAt: typeof config.modifiedAt === 'string' ? config.modifiedAt : null,
  };
};
